import { useEffect, useRef } from 'react';
import { useProject } from '../store/projectContext';
import { useActiveSceneWindow } from './useActiveSceneWindow';

const ASSET_BASE = 'https://dash.animaker.com/a/u/';

function resolveUrl(src) {
  if (!src) return '';
  if (src === 'src/sample/sample.mp4') return '/sample.mp4';
  if (src.startsWith('http') || src.startsWith('/')) return src;
  return `${ASSET_BASE}${src}`;
}

/**
 * Progressively prefetches video URLs for scenes near the current playhead.
 * Uses <link rel="prefetch"> for non-blocking, low-priority background fetching.
 * Only prefetches the next few scenes ahead of the current one.
 * 
 * This replaces the old blocking `Promise.all(prefetch(...))` approach that
 * downloaded ALL videos before showing the editor.
 */
export function useProgressivePrefetch() {
  const project = useProject();
  const { activeSceneIndex } = useActiveSceneWindow(2);
  const prefetchedUrls = useRef(new Set());
  const linkElements = useRef(new Map());

  useEffect(() => {
    if (!project?.scenes || activeSceneIndex < 0) return;

    const scenes = project.scenes;
    // Prefetch the next 3 scenes ahead of current (non-blocking)
    const prefetchAhead = 3;
    const start = activeSceneIndex + 1;
    const end = Math.min(scenes.length, activeSceneIndex + 1 + prefetchAhead);

    for (let i = start; i < end; i++) {
      const scene = scenes[i];
      if (!scene?.video?.src) continue;

      const url = resolveUrl(scene.video.src);
      if (prefetchedUrls.current.has(url)) continue;

      // Create a <link rel="prefetch"> for low-priority background fetch
      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.as = 'video';
      link.href = url;
      link.crossOrigin = 'anonymous';
      document.head.appendChild(link);

      prefetchedUrls.current.add(url);
      linkElements.current.set(url, link);
    }

    // Cleanup: remove prefetch links for scenes we've moved far past
    // to avoid accumulating too many link elements
    const cleanupThreshold = 10;
    if (linkElements.current.size > cleanupThreshold) {
      const scenesToKeep = new Set();
      const keepStart = Math.max(0, activeSceneIndex - 2);
      const keepEnd = Math.min(scenes.length, activeSceneIndex + 1 + prefetchAhead);
      for (let i = keepStart; i < keepEnd; i++) {
        const scene = scenes[i];
        if (scene?.video?.src) {
          scenesToKeep.add(resolveUrl(scene.video.src));
        }
      }

      for (const [url, link] of linkElements.current.entries()) {
        if (!scenesToKeep.has(url)) {
          link.remove();
          linkElements.current.delete(url);
          // Keep in prefetchedUrls so we don't re-add if user scrubs back
        }
      }
    }
  }, [activeSceneIndex, project?.scenes]);

  // Cleanup all links on unmount
  useEffect(() => {
    return () => {
      for (const link of linkElements.current.values()) {
        link.remove();
      }
      linkElements.current.clear();
      prefetchedUrls.current.clear();
    };
  }, []);
}
